import { LocateFixed } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { ErrorMessage } from '../../components/ErrorMessage'
import { OnboardingShell } from '../../components/OnboardingShell'
import { fillIfEmpty, reverseGeocode } from '../../utils/reverseGeocode'
import { isValidPincode, PINCODE_PATTERN } from '../../utils/validation'

/**
 * A learner opening their teaching side.
 *
 * <p>Short on purpose. Name, email, mobile, date of birth and gender are already known about this
 * person, and asking again would only invite two different answers to the same question. What is
 * left is what the platform does not know: what they teach, and where their tutor dot goes.
 *
 * <p>The dot is the reason the location fields are here even though the learner profile already has
 * a pincode. They default to it - a person lives in one place - but a tutor is found by where they
 * teach, which is not always where they study.
 */
export function AddTutorProfilePage() {
  const { profile, addProfile } = useAuth()
  const navigate = useNavigate()

  const [bio, setBio] = useState('')
  const [qualificationsText, setQualifications] = useState('')
  const [pincode, setPincode] = useState('')
  const [locality, setLocality] = useState('')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // The same rules the server enforces, so the offer is not made to someone it would refuse.
  if (profile && (!profile.canAddProfile || profile.persona !== 'DEMAND')) {
    return <Navigate to="/profile" replace />
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      setError('This browser cannot share a location. Please type the pincode instead.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const next = { lat: position.coords.latitude, lng: position.coords.longitude }
        setCoords(next)
        try {
          const address = await reverseGeocode(next.lat, next.lng)
          // Fills the blanks and leaves anything already typed alone - the pin is a convenience,
          // not a correction of what someone deliberately entered.
          setPincode((current) => fillIfEmpty(current, address.pincode))
          setLocality((current) => fillIfEmpty(current, address.area))
        } catch (err) {
          console.error('[add-tutor] could not look up the address', err)
          setError('Location pinned, but the address could not be looked up - please type it in.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setError('Could not get your location. Check the browser has permission, or type it in.')
      },
      { timeout: 8000 },
    )
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (pincode && !isValidPincode(pincode)) {
      setError('Enter a valid 6-digit pincode')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await addProfile({
        bio: bio || undefined,
        qualificationsText: qualificationsText || undefined,
        pincode: pincode || undefined,
        locality: locality || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
      })
      // addProfile leaves the session on the profile just created, so this is the tutor's own
      // profile page - which is also where the verification card lives, and verification is the
      // next thing that has to happen before anyone can find them.
      navigate('/profile', { replace: true })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your tutor profile'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      title="Create your Tutor/Coach/Trainer Profile"
      showcase={false}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-sm text-ink-600">
          Your name, contact details and date of birth carry over - we will not ask again. This is
          just what learners need to know about your teaching.
        </p>

        <label className="block">
          <span className="label">About your teaching</span>
          <textarea
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="input"
            placeholder="What you teach, how you teach it, and who you teach best."
          />
        </label>

        <label className="block">
          <span className="label">Qualifications</span>
          <textarea
            rows={2}
            value={qualificationsText}
            onChange={(e) => setQualifications(e.target.value)}
            className="input"
            placeholder="Degrees, certifications, years of experience."
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">Location pincode</span>
            <input
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              pattern={PINCODE_PATTERN}
              inputMode="numeric"
              className="input"
              placeholder="201001"
            />
            <span className="mt-1 block text-xs text-ink-400">Where your teaching dot goes on the map.</span>
          </label>

          <label className="block">
            <span className="label">Area / locality</span>
            <input
              value={locality}
              onChange={(e) => setLocality(e.target.value)}
              className="input"
              placeholder="e.g. Indirapuram"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={useCurrentLocation}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-700 underline-offset-4 hover:underline"
        >
          <LocateFixed size={15} /> {locating ? 'Locating...' : coords ? 'Location set' : 'Use my current location'}
        </button>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Your teaching profile starts unverified, like any new tutor - being an established learner
          here does not vouch for it. You can send your documents for review from your profile once
          this is created.
        </div>

        <ErrorMessage message={error} />

        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="btn-ghost">
            Back
          </button>
          <button type="submit" disabled={submitting} className="btn-sun flex-1 disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create my tutor profile'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
